import { Injectable } from '@angular/core'

export interface ResponsiveContainerState {
  outsideElements: Element[]
  insideElements: Element[]
}

interface Element {
  width?: number
}

@Injectable({ providedIn: 'root' })
export class ResponsiveFilterService {
  /** A safe space so the filter isn't glued to the side. Will be parameterizable */
  private safeSpacing = 75

  /**
   * Will set the safe spacing
   * @param safeSpacing
   */
  public setSafeSpacing(safeSpacing = 75): void {
    this.safeSpacing = safeSpacing
  }

  /**
   * Will tell you what's inside and what's not
   * @param elements
   * @param containerWidth
   * @param togglerWidth
   */
  public getInsideAndOutsideElementsFromAContainerWidth(
    elements: Element[],
    containerWidth: number,
    togglerWidth: number
  ): ResponsiveContainerState {
    return {
      outsideElements: this.getElementsOutside(
        containerWidth,
        elements,
        togglerWidth
      ),
      insideElements: this.getElementsInside(
        containerWidth,
        elements,
        togglerWidth
      ),
    }
  }

  /**
   * Will get the element outside of the container
   * @param containerWidth
   * @param elements
   * @param togglerWidth
   */
  public getElementsOutside(
    containerWidth: number,
    elements: Element[],
    togglerWidth: number
  ): Element[] {
    return elements.filter((element: Element, index: number) => {
      return (
        element.width === 0 ||
        this.getTotalWidthOfElements(
          [...elements.slice(0, index + 1)],
          togglerWidth
        ) > // Index+1 to count the current element
          containerWidth - this.safeSpacing
      )
    })
  }

  /**
   * Will get the element inside the container
   * @param containerWidth
   * @param elements
   * @param togglerWidth
   */
  public getElementsInside(
    containerWidth: number,
    elements: Element[],
    togglerWidth: number
  ): Element[] {
    return elements.filter((element: Element, index: number) => {
      return (
        element.width !== 0 &&
        this.getTotalWidthOfElements(
          [...elements.slice(0, index + 1)],
          togglerWidth
        ) <= // Index+1 to count the current element
          containerWidth - this.safeSpacing
      )
    })
  }

  /**
   * Will get the total width of the provided elements
   * @param elements
   * @param togglerWidth
   * @private
   */
  private getTotalWidthOfElements(
    elements: Element[],
    togglerWidth: number
  ): number {
    return elements.reduce((previous: number, current: Element) => {
      return previous + (current.width || 0)
    }, togglerWidth)
  }
}
